import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import { ProductEventDataList } from 'corgi-types';
import type { SpaceProps, WidthProps } from 'styled-system';
import {
  display,
  flexWrap,
  gridGap,
  gridTemplateColumns,
  justifyContent,
  space,
  width,
} from 'styled-system';

import type { ProductSuggestionItem } from '../../../global-header/components/toolbar/types';
import { useMatchesViewport } from '../../hooks/media';
import { useLocalization } from '../../providers/LocaleProvider';
import { useObserveArticles } from '../../tracking/useObserveArticles';
import { BodyBoldBB1 } from '../Typography';
import SuggestionProductCard from './SuggestionProductCard';
import type { ProductSuggestionItemProps, SuggestionGetItemProps } from './types';

interface SuggestionsProductListProps {
  productSuggestions: ProductSuggestionItem[];
  getItemProps: SuggestionGetItemProps<ProductSuggestionItemProps>;
  highlightedIndex: number;
  productsStartIndex: number;
}

const PRODUCTS_COUNT = 4;
const PRODUCTS_COUNT_XL = 6;
const DEBOUNCE_TRACKING_VIEW = 1000;

const itemWidth = ['calc((100% - 4px) / 2)', null, null, 'calc((100% - 8px) / 3)'];

const titleProps: ComponentProps<typeof BodyBoldBB1> = {
  as: 'h2',
  color: 'sys.neutral.text.strong',
  px: 'sp_8',
  py: 'sp_12',
  mt: ['sp_8', null, 'sp_0'],
};

const UnstyledContainer = styled.div<SpaceProps & WidthProps>`
  ${space};
  ${width};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    px={[null, null, 'sp_12', 'sp_4']}
    m={[null, null, 'sp_24']}
    mb={['sp_16', 'sp_20', 'sp_24', 'sp_32']}
    width="100%"
    {...props}
  />
);

const UnstyledListContainer = styled.ul<any>`
  ${display};
  ${flexWrap};
  ${gridGap};
  ${gridTemplateColumns};
  ${justifyContent};
  ${space};
`;

const ListContainer = (props: ComponentProps<typeof UnstyledListContainer>) => (
  <UnstyledListContainer
    display="flex"
    flexWrap="wrap"
    gridGap="sp_4"
    gridTemplateColumns={['repeat(2, minmax(0, 1fr))', null, 'repeat(3, minmax(0, 1fr))']}
    m="sp_0"
    p="sp_0"
    {...props}
  />
);

const UnstyledListViewItem = styled.li<any>`
  ${display};
  ${width};
  ${space};
`;

const ListViewItem = (props: ComponentProps<typeof UnstyledListViewItem>) => (
  <UnstyledListViewItem display="inline-block" width={itemWidth} mb="sp_4" {...props} />
);

const transformToTrackingArticle = (productSuggestions: ProductSuggestionItem[]) =>
  productSuggestions.map(({ sku, prices, name }) => ({
    sku,
    tracking: {
      productSimpleSKU: sku,
      productName: name,
      productPrice: prices.special?.value ?? prices.regular?.value,
    },
  }));

const SuggestionsProductList = ({
  productSuggestions,
  getItemProps,
  highlightedIndex,
  productsStartIndex,
}: SuggestionsProductListProps) => {
  const titleId = useId();
  const {
    matches: { matchesLargeDesktop },
  } = useMatchesViewport(false);
  const { t } = useLocalization();
  useObserveArticles({
    articles: transformToTrackingArticle(productSuggestions),
    listType: ProductEventDataList.SearchAutoSuggest,
    dataTestId: 'suggestions-product-item',
    debounce: DEBOUNCE_TRACKING_VIEW,
  });

  return (
    <Container data-testid="suggestions-product-list-container">
      <BodyBoldBB1 id={titleId} {...titleProps}>
        {t('h24_search_suggestion_product_title')}
      </BodyBoldBB1>
      <ListContainer aria-labelledby={titleId}>
        {productSuggestions
          .slice(0, matchesLargeDesktop ? PRODUCTS_COUNT_XL : PRODUCTS_COUNT)
          .map((item, index) => (
            <ListViewItem
              key={item.sku}
              data-cnstrc-item-section="Products"
              data-cnstrc-item-name={item.name}
              data-cnstrc-item-id={item.parentSku}
              data-cnstrc-item-variation-id={item.sku}
            >
              <SuggestionProductCard
                {...item}
                index={index}
                highlightedIndex={highlightedIndex}
                productsStartIndex={productsStartIndex}
                getItemProps={getItemProps}
              />
            </ListViewItem>
          ))}
      </ListContainer>
    </Container>
  );
};

export default SuggestionsProductList;
